import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, useCurrent, Grid } from "./common";
import { Stat, Plate, Meter, Avatar, StatusBadge, PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { useCountUp } from "../lib/anim";
import { money, outstanding, currentStage, stageProgress } from "../lib/store";
import { sendToContractGen } from "../lib/bridge";
import { openWhatsApp } from "../lib/whatsapp";
import { StudioLinks, SyncPanel } from "../components/StudioLinks";
import { InviteClient } from "../components/InviteClient";
import { StudioActions } from "../components/NextSteps";
import { ActionCenter } from "../components/ActionCenter";
import { Copilot } from "../components/Copilot";
import { AgentPanel } from "./Agent";
import { studioTodos } from "../lib/todos";
import { PdfPreview } from "../components/PdfPreview";
import { designerPackDoc, briefMoneyMentions } from "../lib/pdf";
import { useCan } from "../lib/auth";
import { fmtDate, relative, daysUntil } from "../lib/format";

export default function Dashboard() {
  const p = useCurrent();
  const nav = useNavigate();
  const canEdit = useCan("contracts");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  /* Every hook has to run on every render. The project arrives asynchronously —
     the workspace hydrates from D1 after mount — so on a device without it
     cached, `p` is undefined first and defined next. Bailing out before these
     changed the hook count between renders and React tore the page down. */
  const moneyInBrief = p ? briefMoneyMentions(p) : [];
  const progress = p ? stageProgress(p) : 0;
  const owed = p ? outstanding(p) : 0;
  const pctRef = useCountUp(progress, (n) => `${Math.round(n)}%`);
  const owedRef = useCountUp(owed, (n) => money(n, p?.currency ?? "USD"));
  if (!p) return null;
  const stage = currentStage(p);
  const openFb = p.feedback.filter((f) => !f.resolved).length;
  const nextEvent = [...p.schedule].filter((e) => daysUntil(e.date) >= 0).sort((a, b) => +new Date(a.date) - +new Date(b.date))[0];

  return (
    <Page>
      {/* hero */}
      <div className="card reveal" style={{ overflow: "hidden", marginBottom: 22 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr" }}>
          <div style={{ padding: "34px 34px 30px" }}>
            <div className="kicker">{p.type} · {p.location}</div>
            <h1 className="serif" style={{ fontSize: 46, marginTop: 10, lineHeight: 1 }}>{p.name}</h1>
            <p className="muted" style={{ marginTop: 14, maxWidth: 440, fontSize: 15 }}>
              {p.client.company} · {p.area} · Appointed {fmtDate(p.createdOn)}
            </p>
            <div className="row" style={{ gap: 10, marginTop: 22 }}>
              <button className="btn accent" onClick={() => nav(`/studio/${p.id}/presentation`)}><Icon.present width={16} />Open presentation</button>
              <button className="btn soft" onClick={() => setInviteOpen(true)}><Icon.send width={16} />Invite client</button>
              <button className="btn ghost" onClick={() => setPackOpen(true)}><Icon.download width={16} />Designer pack</button>
              <button className="btn ghost" onClick={() => nav(`/portal/${p.id}`)}><Icon.arrowUpRight width={16} />Client view</button>
              <Copilot p={p} />
              {canEdit && <button className="btn soft" onClick={() => sendToContractGen(p)}><Icon.report width={16} />Create contract</button>}
            </div>
            <div style={{ marginTop: 12 }}><StudioLinks p={p} /></div>
          </div>
          <div style={{ position: "relative", minHeight: 240 }}>
            <Plate hue={p.cover} h={260} seed={3} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, var(--card), transparent 40%)" }} />
          </div>
        </div>
      </div>

      <AgentPanel p={p} canEdit={canEdit} />

      <ActionCenter items={studioTodos(p)} base={`/studio/${p.id}`} />

      <div style={{ marginBottom: 22 }}><StudioActions p={p} /></div>

      {/* keeping the project in step with the contract, tender and site rooms */}
      <div className="card pad reveal" style={{ marginBottom: 22 }}>
        <div className="kicker" style={{ marginBottom: 4 }}>Connected apps</div>
        <h3 className="serif" style={{ fontSize: 22, marginBottom: 12 }}>Sync this project</h3>
        <SyncPanel p={p} />
      </div>

      {/* stats */}
      <Grid cols="repeat(4, 1fr)" style={{ marginBottom: 22 }}>
        <Stat label="Stage progress" accent sub={<>Now · <b style={{ color: "var(--ink)" }}>{stage.label}</b></>}><span ref={pctRef}>0%</span></Stat>
        <Stat label="Outstanding" sub={owed > 0 ? "Across open invoices" : "All settled"}><span ref={owedRef}>{money(0, p.currency)}</span></Stat>
        <Stat label="Open feedback" sub={openFb ? "Awaiting studio reply" : "Inbox clear"}>{openFb}</Stat>
        <Stat label="Next" sub={nextEvent ? relative(nextEvent.date) : "—"}>
          <span style={{ fontSize: 20 }}>{nextEvent ? nextEvent.title.split(" ").slice(0, 3).join(" ") : "—"}</span>
        </Stat>
      </Grid>

      <Grid cols="1.5fr 1fr">
        {/* you are here + activity */}
        <div className="col" style={{ gap: 22 }}>
          <div className="card pad reveal">
            <div className="row between" style={{ marginBottom: 18 }}>
              <div><div className="kicker">Where we are</div><h3 className="serif" style={{ fontSize: 22, marginTop: 5 }}>Project journey</h3></div>
              <button className="btn soft sm" onClick={() => nav(`/studio/${p.id}/stages`)}>Details <Icon.arrow width={15} /></button>
            </div>
            <Meter value={progress} />
            <div className="row" style={{ gap: 6, marginTop: 18, flexWrap: "wrap" }}>
              {p.stages.map((s) => (
                <div key={s.key} title={s.blurb} style={{ flex: 1, minWidth: 70 }}>
                  <div style={{ height: 4, borderRadius: 4, background: s.status === "done" ? "var(--accent)" : s.status === "active" ? "var(--ink)" : "var(--line-2)" }} />
                  <div style={{ fontSize: 10.5, marginTop: 7, color: s.status === "upcoming" ? "var(--ink-3)" : "var(--ink)", fontWeight: s.status === "active" ? 600 : 400 }}>{s.label}</div>
                  {s.status === "active" && <div className="badge accent" style={{ marginTop: 5, fontSize: 9.5, padding: "2px 7px" }}>You are here</div>}
                </div>
              ))}
            </div>
          </div>

          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 14 }}>Recent activity</div>
            <div className="col" style={{ gap: 2 }}>
              {p.activity.slice(0, 6).map((a) => (
                <div key={a.id} className="row" style={{ gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
                  <span style={{ width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", background: "var(--paper-2)", color: "var(--accent-ink)", flexShrink: 0 }}>{Icon[actIcon(a.kind)]({ width: 15 })}</span>
                  <div style={{ flex: 1 }}><span style={{ fontWeight: 500 }}>{a.who}</span> <span className="muted">{a.what}</span></div>
                  <span className="dim" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{relative(a.on)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* side: payments + team */}
        <div className="col" style={{ gap: 22 }}>
          <div className="card pad reveal">
            <div className="row between" style={{ marginBottom: 14 }}><div className="kicker">Payments due</div><button className="btn soft sm" onClick={() => nav(`/studio/${p.id}/quotes`)}>All</button></div>
            <div className="col" style={{ gap: 10 }}>
              {p.invoices.filter((i) => i.status === "due" || i.status === "overdue").slice(0, 3).map((i) => (
                <div key={i.id} className="panel row between" style={{ padding: "12px 14px" }}>
                  <div><div style={{ fontWeight: 500, fontSize: 13.5 }}>{i.ref}</div><div className="dim" style={{ fontSize: 11.5 }}>Due {fmtDate(i.dueOn)}</div></div>
                  <div style={{ textAlign: "right" }}><div className="serif" style={{ fontSize: 17 }}>{money(i.amount, p.currency)}</div><StatusBadge status={i.status} /></div>
                </div>
              ))}
              {p.invoices.filter((i) => i.status === "due" || i.status === "overdue").length === 0 && <div className="dim" style={{ fontSize: 13 }}>No payments outstanding.</div>}
            </div>
          </div>

          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 14 }}>Client</div>
            <div className="row" style={{ gap: 12 }}>
              <Avatar name={p.client.name} hue={p.client.avatarHue} size={46} />
              <div><div style={{ fontWeight: 600 }}>{p.client.name}</div><div className="dim" style={{ fontSize: 12.5 }}>{p.client.company}</div></div>
            </div>
            <div className="divider" style={{ margin: "16px 0" }} />
            <div className="col" style={{ gap: 8, fontSize: 13 }}>
              <div className="row between"><span className="dim">Email</span><span>{p.client.email}</span></div>
              <div className="row between"><span className="dim">Budget</span><span>{money(p.budget, p.currency)}</span></div>
              <div className="row between"><span className="dim">Brief</span><StatusBadge status={p.brief.status} /></div>
            </div>
          </div>

          {/* consultants the client has appointed for structural / MEP */}
          <div className="card pad reveal">
            <div className="kicker" style={{ marginBottom: 14 }}>Delivery consultants</div>
            {(p.consultants ?? []).length === 0 ? (
              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.5 }}>The client appoints the structural &amp; MEP engineers from their portal. None appointed yet.</div>
            ) : (
              <div className="col" style={{ gap: 10 }}>
                {(p.consultants ?? []).map((c) => (
                  <div key={c.id} className="panel" style={{ padding: "11px 13px" }}>
                    <div className="row between" style={{ gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="kicker" style={{ fontSize: 8.5 }}>{c.discipline}</div>
                        <div style={{ fontWeight: 600, fontSize: 13.5, marginTop: 2 }}>{c.firm}</div>
                        {c.contact && <div className="dim" style={{ fontSize: 11.5 }}>{c.contact}</div>}
                      </div>
                      <span className="badge ok" style={{ fontSize: 9, flexShrink: 0 }}>client-appointed</span>
                    </div>
                    {(c.email || c.phone) && (
                      <div className="row wrap" style={{ gap: 6, marginTop: 9 }}>
                        {c.email && <a className="btn ghost sm" href={`mailto:${c.email}?subject=${encodeURIComponent(p.name + " — " + c.discipline)}`} style={{ fontSize: 11 }}><Icon.send width={12} />Email</a>}
                        {c.phone && <button className="btn ghost sm" style={{ fontSize: 11 }} onClick={() => openWhatsApp(`Hi ${c.contact || c.firm} — regarding ${p.name}. `, c.phone)}><Icon.chat width={12} />WhatsApp</button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Grid>
      <InviteClient p={p} open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <PdfPreview open={packOpen} onClose={() => setPackOpen(false)} title="Concept Design Pack"
        filename={`${p.name.replace(/[^\w]+/g, "_")}_concept_pack.pdf`} build={() => designerPackDoc(p)}
        notice={moneyInBrief.length ? (
          <div className="panel" style={{ padding: "10px 13px", borderColor: "var(--warn)" }}>
            <div className="row" style={{ gap: 9, alignItems: "flex-start" }}>
              <Icon.bell width={14} style={{ color: "var(--warn)", flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                No fee or budget figures are included — but the client's own brief mentions money in <b>{moneyInBrief.join(", ")}</b>. That's their wording, so it's left as written. Edit it in the Brief if the designer shouldn't see it.
              </span>
            </div>
          </div>
        ) : undefined} />
    </Page>
  );
}

function actIcon(k: string): "brief" | "concept" | "money" | "calendar" | "report" | "chat" | "stages" {
  return ({ brief: "brief", design: "concept", payment: "money", schedule: "calendar", report: "report", feedback: "chat", stage: "stages" } as const)[k] ?? "concept";
}
